/**
 * ConnectWise Hosted API SDK Wrapper
 * Provides a Promise-based interface for the ConnectWise SDK
 */

export class ConnectWiseSDK {
  /**
   * Initialize the ConnectWise SDK
   * @param {string} origin - ConnectWise domain origin (e.g., 'http://127.0.0.1:8888')
   * @param {function} onLoad - Callback for onLoad event
   * @param {boolean} debug - Enable debug logging
   */
  constructor(origin, onLoad, debug = true) {
    this.api = null;
    this.origin = origin;
    this.onLoadCallback = onLoad;
    this.debug = debug;
    this.initialize();
  }

  /**
   * Initialize the SDK with event handlers
   * @private
   */
  initialize() {
    if (!window.ConnectWiseHostedAPI) {
      console.error('ConnectWise SDK not loaded. Make sure ConnectWiseHostedAPI.1.0.js is included in index.html');
      return;
    }

    try {
      this.api = new window.ConnectWiseHostedAPI(
        this.origin,
        {
          eventHandlers: [
            { event: 'onLoad', callback: this.onLoadCallback }
          ]
        },
        this.debug
      );

      if (this.debug) {
        console.log('ConnectWise SDK initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize ConnectWise SDK:', error);
    }
  }

  /**
   * Get member authentication information
   * @returns {Promise<Object>} Authentication data including memberHash and memberContext
   * @example
   * const auth = await sdk.getMemberAuthentication();
   * // Returns: { site, companyid, memberid, memberHash, memberContext, memberEmail }
   */
  getMemberAuthentication() {
    return new Promise((resolve, reject) => {
      if (!this.api) {
        reject(new Error('SDK not initialized'));
        return;
      }

      this.api.post(
        { request: 'getMemberAuthentication' },
        (data) => {
          if (this.debug) {
            console.log('Member authentication received:', data);
          }
          resolve(data);
        }
      );
    });
  }

  /**
   * Set the dirty flag on the ConnectWise form
   * @param {boolean} dirty - Whether the form has unsaved changes
   */
  setDirty(dirty) {
    if (!this.api) {
      console.error('SDK not initialized');
      return;
    }

    this.api.post({
      request: 'setDirty',
      args: { dirty }
    });
  }

  /**
   * Open a ConnectWise screen
   * @param {string} screen - Screen type ('ticket' or 'company')
   * @param {number} id - Record ID to open
   * @param {boolean} newTab - Open in new tab (default: true)
   */
  openScreen(screen, id, newTab = true) {
    if (!this.api) {
      console.error('SDK not initialized');
      return;
    }

    this.api.post({
      request: 'openScreen',
      args: { screen, id, newTab }
    });
  }

  /**
   * Refresh the current ConnectWise screen
   */
  refreshScreen() {
    if (!this.api) {
      console.error('SDK not initialized');
      return;
    }

    this.api.post({
      request: 'refreshScreen',
      args: {}
    });
  }

  /**
   * Check if the SDK is initialized
   * @returns {boolean}
   */
  isInitialized() {
    return this.api !== null;
  }
}
